// Packages and components
import React, { useState } from 'react';
import { Box } from 'grommet';
import ImportTeam from './ImportTeam';
import SHDButton from '@ui/Button/SHDButton';
import SHDFancyRule from '@ui/FancyRule/SHDFancyRule';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDImage from '@ui/Image/SHDImage';
import SHDLogoButton from '@ui/LogoButton/SHDLogoButton';
import SHDModal from '@ui/Modal/SHDModal';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDText from '@ui/Text/SHDText';

// Helpers and utlities

// Types and enums
import { TeamImportPartnerLogo, TeamImportPartnerName } from '@theme/theme.d';
import type { TeamImportPartner } from '@theme/theme.d';
import type { CreateYourTeamProps } from './Onboarding.d';

const CreateYourTeam: React.FC<CreateYourTeamProps> = ({
  onContinue,
  sport,
}) => {
  const importPartners = () => {
    const partners: Array<TeamImportPartner> = [];
    switch (sport) {
      case 'baseball':
      case 'softball':
        partners.push('usssa', 'pg', 'teamsnap');
        break;
      case 'volleyball':
        partners.push('aes', 'teamsnap');
        break;
      case 'basketball':
      case 'lacrosse':
      case 'hockey':
      case 'water_polo':
      case 'soccer':
        partners.push('teamsnap');
        break;
      default:
        break;
    }
    return partners;
  };

  const [selectedPartner, setSelectedPartner] =
    useState<TeamImportPartner | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const getPartnerLogo = (partner: TeamImportPartner) => {
    switch (partner) {
      case 'aes':
        return (
          <SHDImage
            src={TeamImportPartnerLogo.aes}
            alt={`${TeamImportPartnerName.aes} logo`}
            width={90}
            height={28}
          />
        );
      case 'pg':
        return (
          <SHDImage
            src={TeamImportPartnerLogo.pg}
            alt={`${TeamImportPartnerName.pg} logo`}
            width={21}
            height={28}
          />
        );
      case 'teamsnap':
        return (
          <SHDImage
            src={TeamImportPartnerLogo.teamsnap}
            alt={`${TeamImportPartnerName.teamsnap} logo`}
            width={28}
            height={28}
          />
        );
      case 'usssa':
        return (
          <SHDImage
            src={TeamImportPartnerLogo.usssa}
            alt={`${TeamImportPartnerName.usssa} logo`}
            width={28}
            height={28}
          />
        );
    }
  };

  return (
    <SHDModalContainer>
      <Box gap="xlarge">
        <Box gap="xxsmall">
          <SHDHeading level={3}>Create Your Team</SHDHeading>
          <SHDText color="gray-900-alpha-80" style="subhead">
            Enter team information manually or import roster from choices below.
          </SHDText>
        </Box>
        <SHDButton
          primary
          label="Continue with manual team entry"
          onClick={() => onContinue('manual')}
        />
        <SHDFancyRule label="or" />
        <Box gap="xxsmall">
          {importPartners().map((partner) => (
            <>
              <SHDLogoButton
                key={partner}
                label={TeamImportPartnerName[partner]}
                onClick={() => {
                  setSelectedPartner(partner);
                  setShowModal(true);
                }}
                logo={getPartnerLogo(partner)}
              />
            </>
          ))}
        </Box>
        {showModal && (
          <SHDModal isFixedHeight onClose={() => setShowModal(false)}>
            <ImportTeam
              importPartner={selectedPartner}
              sport={sport}
              onContinue={(importTeam) => {
                onContinue(selectedPartner, importTeam);
              }}
            />
          </SHDModal>
        )}
      </Box>
    </SHDModalContainer>
  );
};

export default CreateYourTeam;
